import { createContext, useContext, type ReactNode } from "react";
import { useSelector } from "react-redux";
import { useGetMeQuery } from "../redux/api/user.api";
import type { Me, UserRole } from "../redux/api/user.api";
import type { RulesEngineStore } from "../redux/store";
import { isAuthorized, isAdmin, hasUserRole } from "../utilities/auth";

interface UserContextValue {
  user: Me | undefined;
  isLoading: boolean;
  isUnauthorized: boolean;
  isAdmin: boolean;
  isAuthorized: (roles: UserRole[]) => boolean;
  hasUserRole: boolean;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const token = useSelector((state: RulesEngineStore) => state.auth.token);
  const { data, isLoading, error } = useGetMeQuery(undefined, { skip: !token });

  const user = data?.data;
  const isUnauthorized =
    !!error && "status" in error && Number(error.status) === 401;

  const value: UserContextValue = {
    user,
    isLoading,
    isUnauthorized,
    isAdmin: isAdmin(user),
    isAuthorized: (roles: UserRole[]) => isAuthorized(user, roles),
    hasUserRole: hasUserRole(user),
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return ctx;
}
